import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import shopifyBag from "../../../../assets/img/shopify-bag.svg";
import { useNotification } from "../../../../hooks/useNotification";
import {
  authApiSlice,
  useVerifySignupMutation,
} from "../../../../store/apiSlice/authApiSlice";
import { authenticationSuccess } from "../../../../store/slice/utils";
import { EmptyStateCardDiv, EmptyStateWrapperDiv } from "../index.styles";

/**
 * Completes Shopify-gated signup after the user clicks the email verification link.
 */
export const ShopifySignupVerifyPage = () => {
  const dispatch = useDispatch();
  const notify = useNotification();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const started = useRef(false);

  const [verifySignup, { isError, error, isLoading }] = useVerifySignupMutation();

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    if (!token) {
      notify({ type: "error", message: "Invalid or expired link." });
      navigate("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        const result = await verifySignup({ token }).unwrap();
        const userData = await dispatch(
          authApiSlice.endpoints.autoLogin.initiate(undefined, { forceRefetch: true }),
        ).unwrap();
        dispatch(authenticationSuccess(userData));

        const redirectUrl = result?.redirect_url;
        if (redirectUrl && /^https?:\/\//i.test(redirectUrl)) {
          window.location.assign(redirectUrl);
          return;
        }
        if (redirectUrl && redirectUrl.startsWith("/") && !redirectUrl.startsWith("//")) {
          navigate(redirectUrl, { replace: true });
          return;
        }
        if (result?.config_id) {
          navigate(`/integration/shopify/${result.config_id}/settings`, { replace: true });
          return;
        }
        navigate("/integration/shopify", { replace: true });
      } catch (err) {
        const detail = err?.data?.detail || "Invalid or expired link.";
        notify({ type: "error", message: detail });
        navigate("/login", { replace: true });
      }
    })();
  }, [dispatch, navigate, notify, token, verifySignup]);

  const message = isError
    ? error?.data?.detail || "Invalid or expired link."
    : isLoading
      ? "Verifying your email…"
      : "Finishing signup…";

  return (
    <EmptyStateWrapperDiv>
      <EmptyStateCardDiv>
        <img className="shopifyBrandLogo" src={shopifyBag} alt="Shopify" />
        <div className="emptyHeading">Verifying account</div>
        <div className="emptyDescription">{message}</div>
      </EmptyStateCardDiv>
    </EmptyStateWrapperDiv>
  );
};
